import { useState } from 'react';
import type {
  AccountWithBuckets,
  InsightsSummary,
  Loan,
  UpcomingRecurring,
} from '@financial/shared';
import {
  AccountType,
  LoanKind,
  RecurringDirection,
  businessDate,
  loanRemainingBalance,
  termsRemaining,
} from '@financial/shared';
import { calendarDateToDate, formatCalendarDate, formatMoney } from '../../lib/format';

export interface FinancialPulsePanelProps {
  data: InsightsSummary;
  accounts: AccountWithBuckets[];
  loans: Loan[];
  upcoming: UpcomingRecurring[];
  accountsState: SupportingDataState;
  loansState: SupportingDataState;
  upcomingState: SupportingDataState;
}

type SupportingDataState = 'loading' | 'error' | 'ready';

/**
 * Resumen orientado a decisiones. Los detalles de metodología se ocultan en
 * un <details> nativo para que la tarjeta no compita con la información
 * financiera importante en pantalla pequeña.
 */
export function FinancialPulsePanel({
  data,
  accounts,
  loans,
  upcoming: upcomingItems,
  accountsState,
  loansState,
  upcomingState,
}: FinancialPulsePanelProps) {
  const [upcomingHorizon, setUpcomingHorizon] = useState<7 | 30>(7);
  const liquidTypes = new Set([AccountType.BANK, AccountType.SAVINGS, AccountType.CASH]);
  const liquidAccounts = accounts.filter(
    (account) => account.currency === data.currency && liquidTypes.has(account.type),
  );
  const liquidBalance = liquidAccounts.reduce((total, account) => total + account.balance, 0);
  const reservedLiquidity = liquidAccounts.reduce(
    (total, account) => total + account.buckets.reduce((sum, bucket) => sum + bucket.balance, 0),
    0,
  );
  const freeLiquidity =
    accountsState === 'ready' ? Math.max(0, liquidBalance - reservedLiquidity) : undefined;
  // Usa el mismo total mensual del presupuesto. Sumar la proyección completa
  // de tarjeta volvería a contar cargos fijos que ya viven en
  // `recurringExpenses`.
  const fixedCommitments = data.budget.breakdown.fixedCommitments;
  const coverageMonths =
    freeLiquidity !== undefined && fixedCommitments > 0 ? freeLiquidity / fixedCommitments : null;
  const activeDebts = loans.filter(
    (loan) =>
      loan.currency === data.currency && loan.kind === LoanKind.DEBT && termsRemaining(loan) > 0,
  );
  const loanDebt = activeDebts.reduce((total, loan) => total + loanRemainingBalance(loan), 0);
  const debtBalance = loansState === 'ready' ? loanDebt + data.cardDebt : undefined;
  const categorizedSpent = data.spendingByCategory.reduce(
    (total, category) => total + category.total,
    0,
  );
  const uncategorized =
    data.spendingByCategory.find((category) => category.categoryId === null)?.total ?? 0;
  const uncategorizedRatio = categorizedSpent > 0 ? uncategorized / categorizedSpent : 0;
  const obligationsState = combineStates(loansState, upcomingState);
  const upcoming =
    obligationsState === 'ready'
      ? upcomingObligations(
          data,
          loans.filter((loan) => loan.currency === data.currency),
          upcomingItems,
          upcomingHorizon,
        )
      : [];

  return (
    <section className="mt-4 rounded-[18px] bg-slate-900 p-4 text-white shadow-[0_14px_30px_-16px_rgba(15,23,42,.6)] lg:p-5">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[.08em] text-slate-400">
            Dinero y compromisos
          </p>
          <h2 className="mt-0.5 text-lg font-bold tracking-tight">
            Tus saldos de hoy y el mes elegido
          </h2>
        </div>
        <p className="max-w-sm text-xs leading-5 text-slate-400">
          Las cuentas y deudas son actuales; la cobertura y el gasto usan el mes que elegiste.
        </p>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <PulseMetric
          label="Dinero libre en tus cuentas hoy"
          value={valueForState(accountsState, freeLiquidity)}
          status="Registrado"
          detail={detailForState(
            accountsState,
            'Banco, ahorro y efectivo, después de restar apartados.',
            'No pudimos consultar tus cuentas; no mostramos un cero parcial.',
          )}
        />
        <PulseMetric
          label="Meses de compromisos cubiertos"
          value={
            accountsState !== 'ready'
              ? valueForState(accountsState)
              : coverageMonths === null
                ? 'Sin compromisos'
                : `${coverageMonths.toFixed(1)} meses`
          }
          status="Estimado"
          tone={coverageMonths !== null && coverageMonths < 1 ? 'risk' : 'neutral'}
          detail={detailForState(
            accountsState,
            'Compara el dinero libre de hoy con los compromisos del mes elegido.',
            'Faltan tus cuentas para completar el cálculo.',
          )}
        />
        <PulseMetric
          label="Deuda activa"
          value={valueForState(loansState, debtBalance)}
          status="Estimado"
          tone={debtBalance === undefined ? 'neutral' : debtBalance > 0 ? 'risk' : 'good'}
          detail={detailForState(
            loansState,
            'Combina la tarjeta registrada con el saldo estimado de tus deudas.',
            'No pudimos consultar tus deudas; no mostramos un total parcial.',
          )}
        />
        <PulseMetric
          label="Gasto sin categoría del mes elegido"
          value={formatMoney(uncategorized)}
          status="Registrado"
          tone={uncategorized > 0 ? 'risk' : 'good'}
          detail={`${Math.round(uncategorizedRatio * 100)}% del gasto registrado en el mes elegido.`}
        />
      </div>

      <details className="group mt-3 rounded-xl border border-white/10 bg-white/[.04]">
        <summary className="min-h-11 cursor-pointer list-none px-3.5 py-3 text-sm font-semibold text-slate-200 marker:content-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900">
          <span aria-hidden="true">ⓘ </span>Qué incluye cada dato
          <span
            className="float-right text-slate-400 transition-transform group-open:rotate-180"
            aria-hidden="true"
          >
            ⌄
          </span>
        </summary>
        <div className="grid gap-3 border-t border-white/10 px-3.5 py-3 text-xs leading-5 text-slate-300 sm:grid-cols-2">
          <p>
            <strong className="text-white">Dinero libre hoy.</strong> Suma sólo cuentas en{' '}
            {data.currency} y resta el dinero que ya destinaste a apartados.
          </p>
          <p>
            <strong className="text-white">Meses cubiertos.</strong> Divide ese dinero libre entre
            MSI previas, deudas, cargos fijos y gastos planeados del mes elegido.
          </p>
          <p>
            <strong className="text-white">Deuda activa.</strong> Suma el saldo registrado de la
            tarjeta y una estimación de tus deudas en {data.currency}; es distinto del pago que toca
            este mes.
          </p>
          <p>
            <strong className="text-white">Gasto sin categoría.</strong> Muestra movimientos y
            compras que todavía necesitan una categoría.
          </p>
        </div>
      </details>

      <section
        className="mt-3 rounded-xl border border-white/10 bg-white/[.06] p-3.5"
        aria-labelledby="upcoming-payments-heading"
      >
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <h3 id="upcoming-payments-heading" className="text-sm font-semibold">
              Próximos compromisos desde hoy
            </h3>
            <DarkStatusBadge status="Estimado" />
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex rounded-md bg-white/10 p-0.5 text-xs font-semibold"
              role="group"
              aria-label="Horizonte de próximos compromisos"
            >
              {([7, 30] as const).map((days) => (
                <button
                  key={days}
                  type="button"
                  aria-pressed={upcomingHorizon === days}
                  onClick={() => setUpcomingHorizon(days)}
                  className={`min-h-11 rounded px-2 transition-colors ${upcomingHorizon === days ? 'bg-white text-slate-900' : 'text-slate-200 hover:text-white'}`}
                >
                  {days} días
                </button>
              ))}
            </div>
            {obligationsState === 'ready' && (
              <span className="text-xs text-slate-300">
                {upcoming.length} {upcoming.length === 1 ? 'pago' : 'pagos'}
              </span>
            )}
          </div>
        </div>
        {obligationsState === 'loading' ? (
          <p className="text-sm text-slate-300" role="status">
            Consultando pagos con fecha registrada…
          </p>
        ) : obligationsState === 'error' ? (
          <p className="text-sm text-red-300" role="status">
            No pudimos completar los próximos pagos. No mostramos una lista parcial.
          </p>
        ) : upcoming.length === 0 ? (
          <p className="text-sm text-slate-400">No hay pagos con fecha registrada.</p>
        ) : (
          <ul className="space-y-2">
            {upcoming.slice(0, 4).map((payment) => (
              <li key={payment.key} className="flex items-center justify-between gap-3 text-sm">
                <span className="min-w-0 truncate text-slate-200">
                  {payment.label}{' '}
                  <span className="text-xs text-slate-400">
                    · {formatCalendarDate(payment.calendarDate)}
                  </span>
                </span>
                <span className="shrink-0 font-semibold tabular-nums text-white">
                  {formatMoney(payment.amount)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </section>
  );
}

function PulseMetric({
  label,
  value,
  detail,
  status,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  detail: string;
  status: 'Registrado' | 'Estimado';
  tone?: 'good' | 'risk' | 'neutral';
}) {
  const valueColor =
    tone === 'good' ? 'text-emerald-400' : tone === 'risk' ? 'text-red-400' : 'text-white';
  return (
    <div className="rounded-xl border border-white/10 bg-white/[.06] p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-[11px] font-bold uppercase tracking-[.06em] text-slate-400">{label}</p>
        <DarkStatusBadge status={status} />
      </div>
      <p className={`mt-1 text-xl font-bold tracking-tight tabular-nums ${valueColor}`}>{value}</p>
      <p className="mt-1 text-xs leading-4 text-slate-400">{detail}</p>
    </div>
  );
}

function DarkStatusBadge({ status }: { status: 'Registrado' | 'Estimado' }) {
  const style =
    status === 'Registrado'
      ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'
      : 'border-blue-300/30 bg-blue-300/10 text-blue-200';
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[.05em] ${style}`}
    >
      {status}
    </span>
  );
}

function combineStates(...states: SupportingDataState[]): SupportingDataState {
  if (states.includes('error')) return 'error';
  if (states.includes('loading')) return 'loading';
  return 'ready';
}

function valueForState(state: SupportingDataState, value?: number): string {
  if (state === 'loading') return 'Cargando…';
  if (state === 'error' || value === undefined) return 'No disponible';
  return formatMoney(value);
}

function detailForState(state: SupportingDataState, readyText: string, errorText: string): string {
  if (state === 'loading') return 'Estamos completando esta cifra.';
  if (state === 'error') return errorText;
  return readyText;
}

function upcomingObligations(
  data: InsightsSummary,
  loans: Loan[],
  upcomingItems: UpcomingRecurring[],
  horizonDays: number,
) {
  const now = businessDate(new Date());
  const horizon = new Date(now);
  horizon.setUTCDate(horizon.getUTCDate() + horizonDays);
  const recurringPayments = upcomingItems
    .filter((item) => item.direction !== RecurringDirection.INCOME)
    .map((item) => {
      return {
        key: `recurring-${item.id}`,
        label: item.name,
        amount: item.amount,
        calendarDate: item.dueDate,
        date: calendarDateToDate(item.dueDate),
      };
    });
  const loanPayments = loans
    .filter((loan) => loan.kind === LoanKind.DEBT && termsRemaining(loan) > 0 && loan.nextDueDate)
    .map((loan) => ({
      key: `loan-${loan.id}`,
      label: loan.name,
      amount: loan.monthlyPayment,
      calendarDate: loan.nextDueDate!,
      date: calendarDateToDate(loan.nextDueDate!),
    }));
  const cardPayment = data.cardProjection
    ? [
        {
          key: 'card',
          label: 'Pago de tarjeta',
          amount: data.cardProjection.total,
          calendarDate: data.cardProjection.cycle.paymentDue,
          date: calendarDateToDate(data.cardProjection.cycle.paymentDue),
        },
      ]
    : [];

  return [...recurringPayments, ...loanPayments, ...cardPayment]
    .filter((payment) => payment.date >= now && payment.date <= horizon)
    .sort((a, b) => a.date.getTime() - b.date.getTime());
}
